
import { useState, useEffect } from "react";
import { Check, HelpCircle, X } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";

interface PricingTier {
  name: string;
  monthlyPrice: number;
  annualPrice: number;
  description: string;
  features: {
    text: string;
    included: boolean;
    tooltip?: string;
  }[];
  cta: string;
  popular?: boolean;
}

const tiers: PricingTier[] = [
  {
    name: "Basic",
    monthlyPrice: 19.99,
    annualPrice: 191.90, // 20% discount applied
    description: "Perfect for small teams getting started with analytics",
    features: [
      { text: "Unlimited dashboards", included: true },
      { text: "Core integrations", included: true },
      { text: "CSV/TSV data import", included: true },
      { text: "Dashboard templates", included: true },
      { text: "Data export", included: true },
      { text: "Multi-tenant architecture", included: true },
      { text: "Support chatbot", included: true, tooltip: "AI-powered for app usage help only" },
      { text: "AI data insights", included: false, tooltip: "Advanced AI features for analyzing your data" },
      { text: "Predictive analytics", included: false, tooltip: "ML-powered forecasting and trend analysis" },
    ],
    cta: "Start with Basic",
  },
  {
    name: "Pro",
    monthlyPrice: 49.99,
    annualPrice: 479.90, // 20% discount applied
    description: "Advanced features for growing businesses",
    features: [
      { text: "Unlimited dashboards", included: true },
      { text: "All integrations", included: true },
      { text: "CSV/TSV data import", included: true },
      { text: "Dashboard templates", included: true },
      { text: "Data export", included: true },
      { text: "Multi-tenant architecture", included: true },
      { text: "Support chatbot", included: true, tooltip: "AI-powered for both app usage and data insights" },
      { text: "AI data insights", included: true, tooltip: "Advanced AI features for analyzing your data" },
      { text: "Predictive analytics", included: true, tooltip: "ML-powered forecasting and trend analysis" },
    ],
    cta: "Start with Pro",
    popular: true,
  },
  {
    name: "Enterprise",
    monthlyPrice: 0,
    annualPrice: 0,
    description: "Custom solutions for large organizations",
    features: [
      { text: "Unlimited dashboards", included: true },
      { text: "All integrations + custom", included: true },
      { text: "CSV/TSV data import", included: true },
      { text: "Dashboard templates", included: true },
      { text: "Data export", included: true },
      { text: "Multi-tenant architecture", included: true },
      { text: "Support chatbot", included: true, tooltip: "AI-powered with priority support" },
      { text: "AI data insights", included: true, tooltip: "Advanced AI features for analyzing your data" },
      { text: "Predictive analytics", included: true, tooltip: "ML-powered forecasting and trend analysis" },
    ],
    cta: "Contact Sales",
  },
];

export function Pricing() {
  const [isAnnual, setIsAnnual] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    
    const section = document.getElementById("pricing");
    if (section) {
      observer.observe(section);
    }
    
    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  return (
    <section id="pricing" className="py-20 bg-gray-50 dark:bg-slate-950">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
            Choose the plan that fits your business needs. Start with our 14-day free trial.
          </p>
          
          <div className="flex items-center justify-center mb-6">
            <span className={`mr-3 ${isAnnual ? "text-gray-500" : "font-medium"}`}>
              Monthly
            </span>
            <button
              onClick={() => setIsAnnual(!isAnnual)}
              className="relative w-14 h-7 bg-gray-200 dark:bg-gray-700 rounded-full p-1 transition-all duration-300"
            >
              <span
                className={`absolute w-5 h-5 rounded-full bg-primary transition-all duration-300 ${
                  isAnnual ? "left-8" : "left-1"
                }`}
              ></span>
            </button>
            <span className={`ml-3 ${isAnnual ? "font-medium" : "text-gray-500"}`}>
              Annual <span className="text-green-500 text-sm">Save 20%</span>
            </span>
          </div>
        </div>

        <div className={`grid md:grid-cols-3 gap-8 max-w-5xl mx-auto transition-all duration-1000 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
        }`}>
          {tiers.map((tier, i) => (
            <div
              key={i}
              className={`relative rounded-2xl overflow-hidden transition-all duration-500 ${
                tier.popular
                  ? "shadow-lg transform md:-translate-y-4 border-2 border-primary bg-white dark:bg-slate-900"
                  : "border border-gray-200 dark:border-gray-800 bg-white dark:bg-slate-900 shadow-sm hover:shadow-md"
              }`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              {tier.popular && (
                <div className="bg-primary text-white text-sm py-1 text-center font-medium">
                  Most Popular
                </div>
              )}
              
              <div className="p-6 md:p-8">
                <h3 className="text-2xl font-bold mb-1">{tier.name}</h3>
                <p className="text-gray-600 dark:text-gray-400 min-h-[40px]">
                  {tier.description}
                </p>
                
                <div className="mt-6 mb-6">
                  {tier.name === "Enterprise" ? (
                    <div>
                      <span className="text-3xl font-bold">Custom</span>
                      <p className="text-gray-500 dark:text-gray-400 mt-1">
                        Tailored to your needs
                      </p>
                    </div>
                  ) : (
                    <div>
                      <span className="text-3xl font-bold">
                        ${isAnnual ? tier.annualPrice.toFixed(2) : tier.monthlyPrice.toFixed(2)}
                      </span>
                      <span className="text-gray-500 dark:text-gray-400">
                        {isAnnual ? "/year" : "/month per user"}
                      </span>
                      {isAnnual && (
                        <p className="text-green-500 text-sm mt-1">
                          ${(tier.monthlyPrice * 12 - tier.annualPrice).toFixed(2)} savings
                        </p>
                      )}
                    </div>
                  )}
                </div>
                
                <Link to="/signup">
                  <Button
                    className={`w-full ${
                      tier.popular ? "bg-primary hover:bg-primary/90" : ""
                    }`}
                    variant={tier.popular ? "default" : "outline"}
                  >
                    {tier.cta}
                  </Button>
                </Link>
                
                <ul className="mt-8 space-y-4">
                  {tier.features.map((feature, j) => (
                    <li key={j} className="flex items-start">
                      {feature.included ? (
                        <Check className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      ) : (
                        <X className="h-5 w-5 text-gray-300 dark:text-gray-600 mr-2 shrink-0 mt-0.5" />
                      )}
                      <span
                        className={`text-sm ${
                          feature.included
                            ? "text-gray-700 dark:text-gray-300"
                            : "text-gray-500 dark:text-gray-500"
                        }`}
                      >
                        {feature.text}
                        {feature.tooltip && (
                          <TooltipProvider delayDuration={300}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <HelpCircle className="h-3.5 w-3.5 inline-block ml-1 text-gray-400" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs text-xs">{feature.tooltip}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-12 text-center text-gray-600 dark:text-gray-400">
          <p>
            Need a custom plan? <a href="#" className="text-primary font-medium">Contact our sales team</a>
          </p>
        </div>
      </div>
    </section>
  );
}
